import { useEffect, useState } from "react";
import { READ_ONLY_TITLE, useReadOnly, useWorkshop, useStore } from "@/state/store";
import { api } from "@/api/client";
import { PresenterTopicsView } from "./PresenterTopicsView";
import { WinnerRevealOverlay } from "./WinnerRevealOverlay";
import { TeamSummaryOverlay } from "./TeamSummaryOverlay";
import { BigTimer } from "./BigTimer";
import { EvalCarousel } from "./EvalCarousel";
import { OutputCarousel } from "./OutputCarousel";
import { useWinningTopic } from "@/hooks/useWinningTopic";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { useSlideState } from "@/state/slide-context";
import { ThemenfindungStatus, istThemenfindung } from "./ThemenfindungStatus";
import { nextPhase } from "./operator-helpers";
import type { Phase } from "@offsite/shared";

/**
 * Returns a component to overlay the current slide, or null when the
 * presenter should see the normal slide flow. Triggered by workshop phases.
 */
export function PresenterWorkshopOverlay() {
  const { phase } = useWorkshop();
  return (
    <>
      {renderStage(phase.phase)}
      <OperatorAdvance current={phase.phase} />
    </>
  );
}

function renderStage(phase: Phase) {
  switch (phase) {
    case "topics":
    case "voting":
      return <PresenterTopicsView />;
    case "winner-reveal":
      return <WinnerRevealOverlay />;
    case "grouping":
      return <TeamSummaryOverlay />;
    case "working":
      return <WorkingOverlay />;
    case "submitted":
      return <SubmittedOverlay />;
    case "evaluating":
      return <EvalCarousel phase="evaluating" />;
    case "evaluated":
      return <EvalCarousel phase="evaluated" />;
    case "overnight":
      return <OutputCarousel phase="overnight" />;
    case "reveal":
      return <OutputCarousel phase="reveal" />;
    default:
      return null;
  }
}

/**
 * Persistente Operator-Notbremse: springt aus jeder Phase zur nächsten,
 * unabhängig davon, ob der aktuelle Schritt fertig ist (z.B. bei hängender
 * KI-Bewertung). Reine Präsentations-Zustands-Schaltung — löst keine Lambdas
 * aus; die Happy-Path-Buttons (Timer/Bewertung starten) bleiben bestehen.
 */
function OperatorAdvance({ current }: { current: Phase }) {
  const { state, dispatch } = useStore();
  const readOnly = useReadOnly();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const target = nextPhase(current);
  if (!target) return null;
  const go = async () => {
    setBusy(true);
    setErr(false);
    try {
      const p = await api.phase.set(state.auth.token, target);
      dispatch({ type: "phase/set", phase: p });
    } catch (e) {
      console.error(e);
      setErr(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      className="operator-advance"
      onClick={go}
      disabled={busy || readOnly}
      title={
        readOnly
          ? READ_ONLY_TITLE
          : "Weiter zur nächsten Phase (ohne auf Fertigstellung zu warten)"
      }
    >
      {err ? "Fehler — nochmal?" : "Weiter →"}
    </button>
  );
}

function WorkingOverlay() {
  const { phase } = useWorkshop();
  const { state, dispatch } = useStore();
  const readOnly = useReadOnly();
  const endsAt = phase.timerEndsAt;
  const winner = useWinningTopic();
  const {
    themenfindung: themenfindungCfg,
    sperreBisUhr,
    arbeitsphaseVorschau: Vorschau,
    arbeitsphaseMinuten,
  } = useOffsiteConfig();
  const [chosenMin, setChosenMin] = useState(arbeitsphaseMinuten ?? 15);
  const [busy, setBusy] = useState(false);
  const [submitCount, setSubmitCount] = useState<{ submitted: number; total: number } | null>(null);
  // Tag 1 (netzwirtschaft): Themenfindung statt Einreichungs-Zähler. Die
  // Folie ist die, von der aus der Moderator in die Phase gewechselt ist.
  const folie = useSlideState().index;
  const themenfindung = istThemenfindung(themenfindungCfg, folie);

  const token = state.auth.token;
  useEffect(() => {
    if (!token || !endsAt || themenfindung) return;
    let cancelled = false;
    const load = async () => {
      try {
        const teamsRes = await api.teams.list(token);
        if (cancelled) return;
        const reqs = await Promise.all(
          teamsRes.teams.map(async (t) => {
            try {
              const r = await api.teams.getReq(token, t.number);
              return r.requirement?.status ?? "empty";
            } catch {
              return "empty";
            }
          }),
        );
        if (cancelled) return;
        const submitted = reqs.filter((s) => s === "submitted").length;
        setSubmitCount({ submitted, total: teamsRes.teams.length });
      } catch {
        /* swallow */
      }
    };
    load();
    const id = setInterval(load, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, endsAt, themenfindung]);

  const startTimer = async (minutes: number) => {
    setBusy(true);
    try {
      const p = await api.phase.set(state.auth.token, "working", minutes * 60);
      dispatch({ type: "phase/set", phase: p });
    } finally {
      setBusy(false);
    }
  };

  const extendTimer = async (minutes: number) => {
    setBusy(true);
    try {
      const r = await api.admin.setTimer(state.auth.token, minutes * 60, "extend");
      dispatch({ type: "phase/set", phase: r.phase });
    } finally {
      setBusy(false);
    }
  };

  const closeSubmission = async () => {
    const p = await api.phase.set(state.auth.token, "submitted");
    dispatch({ type: "phase/set", phase: p });
  };

  const PRESETS = [5, 10, 15, 20, 30];

  return (
    <div className={`work-root${themenfindung && endsAt ? " is-themenfindung" : ""}`}>
      <div className="work-bg" aria-hidden="true" />
      <header className="rise rise-1" style={{ textAlign: "center" }}>
        <div className="deck-eyebrow" style={{ color: "var(--svo-blau)" }}>
          {themenfindung ? "Workshop · Themenfindung" : "Workshop · Arbeitsphase"}
        </div>
        {winner && (
          <div
            style={{
              marginTop: 12,
              fontSize: "clamp(1rem, 1.6vw, 1.25rem)",
              color: "rgba(255,255,255,0.7)",
              letterSpacing: "0.04em",
            }}
          >
            Thema:{" "}
            <strong style={{ color: "var(--svo-blau)", fontWeight: 600 }}>
              {winner.topic.title}
            </strong>
          </div>
        )}
        <h1
          className="deck-title"
          style={{
            color: "var(--svo-weiss)",
            fontSize: "clamp(2rem, 4vw, 3.5rem)",
            margin: "8px 0 0",
          }}
        >
          {themenfindung
            ? endsAt
              ? "Findet euren Anwendungsfall."
              : "Bereit für die Themenfindung."
            : endsAt
              ? "Die Teams schreiben ihre Anforderung."
              : "Bereit für die Arbeitsphase."}
        </h1>
      </header>

      <div className="center rise rise-2">
        {endsAt && themenfindung ? (
          <div className="themenfindung-center">
            <BigTimer endsAt={endsAt} variant="dark" label="Verbleibend" />
            <ThemenfindungStatus />
          </div>
        ) : endsAt ? (
          <>
            <BigTimer endsAt={endsAt} variant="dark" label="Verbleibend" />
            {submitCount && submitCount.total > 0 && (
              <div className="work-submit-counter">
                <span className="work-submit-counter-label">Eingereicht</span>
                <span className="work-submit-counter-value">
                  {submitCount.submitted}
                  <span className="work-submit-counter-sep"> / </span>
                  {submitCount.total}
                </span>
                <span className="work-submit-counter-meta">
                  {submitCount.submitted === submitCount.total
                    ? "Alle Teams fertig"
                    : `${submitCount.total - submitCount.submitted} offen`}
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="work-timer-setup">
            {Vorschau && <Vorschau />}
            <div className="work-timer-setup-label">Dauer wählen</div>
            <div className="work-timer-setup-presets">
              {PRESETS.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`work-preset${chosenMin === m ? " active" : ""}`}
                  onClick={() => setChosenMin(m)}
                  disabled={busy}
                >
                  {m} min
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`btn btn-success work-timer-cta${readOnly ? " is-readonly" : ""}`}
              onClick={() => startTimer(chosenMin)}
              disabled={busy || readOnly}
              title={readOnly ? READ_ONLY_TITLE : undefined}
            >
              {busy ? "…" : `→ Timer starten · ${chosenMin} min`}
            </button>
            <div className="work-timer-hint">
              {sperreBisUhr
                ? "Die Handys sind gesperrt — sie öffnen sich, sobald du den Timer startest."
                : "Teilnehmer können bereits jetzt schreiben — der Timer läuft erst, sobald du startest."}
            </div>
          </div>
        )}
      </div>

      <div className="actions rise rise-3">
        {endsAt && (
          <button
            type="button"
            className={`btn-ghost-on-dark${readOnly ? " is-readonly" : ""}`}
            onClick={() => extendTimer(5)}
            disabled={busy || readOnly}
            title={readOnly ? READ_ONLY_TITLE : undefined}
          >
            + 5 min verlängern
          </button>
        )}
        <button
          type="button"
          className={`btn-ghost-on-dark${readOnly ? " is-readonly" : ""}`}
          onClick={closeSubmission}
          disabled={readOnly}
          title={readOnly ? READ_ONLY_TITLE : undefined}
        >
          Einreichung jetzt schließen
        </button>
      </div>
    </div>
  );
}

function SubmittedOverlay() {
  const { state, dispatch } = useStore();
  const readOnly = useReadOnly();
  const trigger = async () => {
    await api.evaluations.trigger(state.auth.token);
    const p = await api.phase.set(state.auth.token, "evaluating");
    dispatch({ type: "phase/set", phase: p });
  };
  return (
    <CenteredMessage
      label="Workshop · Einreichung"
      title="Alle Anforderungen sind eingereicht."
      subtitle="Klick den Button um die KI-Bewertung zu starten — alle Teams werden parallel bewertet."
      cta={{
        label: "Bewertung starten",
        onClick: trigger,
        disabled: readOnly,
        disabledTitle: READ_ONLY_TITLE,
      }}
    />
  );
}

function CenteredMessage({
  label,
  title,
  subtitle,
  cta,
  pulse,
}: {
  label: string;
  title: string;
  subtitle?: string;
  cta?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    disabledTitle?: string;
  };
  pulse?: boolean;
}) {
  return (
    <div className="center-msg">
      <div className="center-msg-bg" aria-hidden="true" />
      <div className="center-msg-inner">
        <div className="center-msg-eyebrow rise rise-1">{label}</div>
        <h1 className="rise rise-2">{title}</h1>
        {subtitle && <p className="rise rise-3">{subtitle}</p>}
        {pulse && <div className="center-msg-pulse rise rise-4" aria-hidden="true" />}
        {cta && (
          <button
            type="button"
            className={`btn btn-success rise rise-4${cta.disabled ? " is-readonly" : ""}`}
            style={{ marginTop: 8, padding: "12px 24px", fontSize: 15 }}
            onClick={cta.onClick}
            disabled={cta.disabled}
            title={cta.disabled ? cta.disabledTitle : undefined}
          >
            {cta.label}
          </button>
        )}
      </div>
    </div>
  );
}
